import {
  sluggedIdSchema,
  sluggedIdValidator,
  createdSchema,
  nameSchema,
  descriptionSchema,
  isoDateWithOffsetValidator,
} from "entities/common";
import { z } from "zod";
import { DateTime } from "luxon";
import type { SluggedId } from "Models";
import {
  TourType,
  EventsWindSpeed,
  HandicapMode,
  TeeType,
  EventMaxPerHoleType,
  GimmeDistance,
  HandicapPercentage,
  Sky,
  Ground,
  WindDirection,
  PinType,
  EventRoundSkillLevel,
  EventScoringType,
  EventTypeEnum,
} from "types/enums";

// Define RankType enum (based on your sample data)
export enum RankType {
  NONE = "none",
  GROUP_SKINS = "group_skins",
  COMBINED_STROKE = "combined_stroke",
  COMBINED_CTP = "combined_ctp",
  COMBINED_LD = "combined_ld",
  COMBINED_GAME = "combined_game",
  // Add other rank types as needed
}

// Types for each component of the event
type InternalEventControlsDto = {
  id: SluggedId;
  eventId: SluggedId;
  gimmes: GimmeDistance;
  mulligans: boolean;
  addStrokes: boolean;
  removeStrokes: boolean;
  applyHandicaps: boolean;
  handicapMode: HandicapMode;
  handicapLevel: HandicapPercentage;
  handicapCourseSlope: boolean;
  maxPerHole: EventMaxPerHoleType;
  userSwingProfile: boolean;
};

export type InternalEventRoundDto = {
  id: SluggedId;
  eventId: SluggedId;
  roundNumber: number;
  courseId?: number;
  courseName?: string;
  swingType?: string;
  skillLevel: EventRoundSkillLevel;
  windSpeed: EventsWindSpeed;
  windDirection: WindDirection;
  holes?: number[];
  pin: PinType;
  sky: Sky;
  ground: Ground;
  mensTees: TeeType;
  womensTees: TeeType;
};

type InternalEventDto = {
  id: SluggedId;
  tourId: SluggedId;
  tourName: string;
  tourIsPublic?: boolean;
  name: string;
  description: string;
  imageUrl: string;
  startTime: DateTime;
  endTime: DateTime;
  eventTimezone?: string;
  created: DateTime;
  numberOfRounds: number;
  replays: number;
  maxUserCount: number;
  eventType: EventTypeEnum;
  scoringType: EventScoringType;
  finalized: boolean;
  participantCount: number;
  finishedCount: number;
  controls: InternalEventControlsDto;
  rounds: InternalEventRoundDto[];
  availableToSimulators?: string[];
};

type InternalTourDto = {
  id: SluggedId;
  name: string;
  type: TourType | string;
  rankType: RankType | string;
  public: boolean;
  facilityId?: string | null;
  created: DateTime;
  eventCount: number;
  completedEventCount: number;
  currentEventCount: number;
};

type InternalIndoorGolfPlayerDto = {
  userId: SluggedId;
  userGuid?: string;
  userName: string;
  firstName?: string;
  lastName?: string;
  isDeleted: boolean;
};

const IndoorGolfPlayerSchema = z.object({
  userId: sluggedIdValidator,
  userGuid: z.uuid().optional(),
  userName: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  isDeleted: z.boolean(),
}) satisfies z.ZodType<InternalIndoorGolfPlayerDto>;

const EventLeaderboardResultsBaseSchema = z.object({
  eventResultId: sluggedIdValidator.optional(),
  eventId: sluggedIdValidator.optional(),
  player: IndoorGolfPlayerSchema.nullable().optional(),
  isFinalized: z.boolean(),
  placeDisplay: z.string(),
  place: z.number().int().nonnegative(),
  totalAttempts: z.number().int().optional(),
  bestAttempt: z.number().int().optional(),
  attemptDateTime: z.union([isoDateWithOffsetValidator, z.null()]).optional(),
  inProgress: z.string().optional(),
  attemptStatus: z.string().optional(),
  facilityId: z.string().optional(),
  facilityName: z.string().optional(),
  simulatorId: z.string().optional(),
  simulatorName: z.string().optional(),
  shotVideoId: sluggedIdValidator.optional(),
  shotVideoUrl: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true; // Optional field, undefined/null is fine

        // Try to validate as URL
        try {
          new URL(val);
          return true; // Valid URL
        } catch {
          // Not a valid URL, but we'll accept it with a warning
          console.warn(
            `⚠️ shotVideoUrl validation: Expected valid URL but got "${val}". Accepting value but this may cause issues in video playback.`,
          );
          return true; // Accept anyway for backward compatibility
        }
      },
      {
        error: "shotVideoUrl should be a valid URL",
      },
    ),
});

// Base type for EventLeaderboardResultsDto
type InternalEventLeaderboardResultsDto = {
  eventResultId?: SluggedId;
  eventId?: SluggedId;
  player?: z.infer<typeof IndoorGolfPlayerSchema> | null;
  isFinalized: boolean;
  placeDisplay: string;
  place: number;
  totalAttempts?: number;
  bestAttempt?: number;
  attemptDateTime?: DateTime;
  inProgress?: string;
  attemptStatus?: string;
  facilityId?: string;
  facilityName?: string;
  simulatorId?: string;
  simulatorName?: string;
  shotVideoId?: SluggedId;
  shotVideoUrl?: string;
};

type InternalSkilledEventLeaderboardResultsDto =
  InternalEventLeaderboardResultsDto & {
    distance: number; // Computed property
  };

type InternalTournamentEventLeaderboardResultsDto =
  InternalEventLeaderboardResultsDto & {
    tournamentResultId: SluggedId;
    scoreToPar: number; // Computed property
    roundsCompleted?: number;
    totalStrokes?: number;
    netTotalStrokes?: number;
    netScoreToPar?: number;
    initialAttempt?: DateTime;
  };

const SkilledEventLeaderboardResultsRawSchema =
  EventLeaderboardResultsBaseSchema.extend({
    // No additional properties needed - distance is computed from score
    distance: z.number(),
  });

// TournamentEventLeaderboardResults schema
const TournamentEventLeaderboardResultsRawSchema =
  EventLeaderboardResultsBaseSchema.extend({
    tournamentResultId: sluggedIdValidator,
    scoreToPar: z.number().int(),
    roundsCompleted: z.number().int().optional(),
    totalStrokes: z.number().int().optional(),
    netTotalStrokes: z.number().int().optional(),
    netScoreToPar: z.number().int().optional(),
    initialAttempt: z.union([isoDateWithOffsetValidator, z.null()]).optional(),
  });

// Define schemas for each component
const EventControlsRawSchema = z.object({
  ...sluggedIdSchema,
  eventId: sluggedIdValidator,
  gimmes: z.enum(GimmeDistance),
  mulligans: z.boolean(),
  addStrokes: z.boolean(),
  removeStrokes: z.boolean(),
  applyHandicaps: z.boolean(),
  handicapMode: z.enum(HandicapMode),
  handicapLevel: z.union([
    z.enum(HandicapPercentage), // Accept string values
    z.number().transform((n) => String(n) as unknown as HandicapPercentage), // Convert numbers to strings
  ]),
  handicapCourseSlope: z.boolean(),
  userSwingProfile: z.boolean(),
  maxPerHole: z.enum(EventMaxPerHoleType),
});

const EventRoundRawSchema = z
  .object({
    ...sluggedIdSchema,
    courseName: z.string().optional(),
    eventId: sluggedIdValidator,
    roundNumber: z.number().int().nonnegative(),
    courseId: z.number().int().nonnegative().nullish(),
    swingType: z.string().nullish(),
    skillLevel: z.enum(EventRoundSkillLevel),
    windSpeed: z.enum(EventsWindSpeed),
    windDirection: z.enum(WindDirection),
    holes: z.array(z.number().int().min(1).max(18)).nullish(),
    pin: z.enum(PinType),
    sky: z.enum(Sky),
    ground: z.enum(Ground),
    mensTees: z.enum(TeeType),
    womensTees: z.enum(TeeType),
  })
  .superRefine((data, ctx) => {
    // If courseId is provided, courseName must also be provided
    if (data.courseId != null && !data.courseName) {
      ctx.addIssue({
        code: "custom",
        message: "courseName must be provided when courseId is set",
        path: ["courseName", "courseId"],
        input: data,
      });
    }
  });

// Base schema with all required fields
const TourDtoBaseSchema = z.object({
  ...sluggedIdSchema,
  ...nameSchema,
  type: z.enum(TourType),
  rankType: z.enum(RankType),
  public: z.boolean(),
  ...createdSchema,
  eventCount: z.number().int().nonnegative(),
  completedEventCount: z.number().int().nonnegative(),
  currentEventCount: z.number().int().nonnegative(),
});

// Create a schema with conditional facilityId validation
const TourDtoRawSchema = TourDtoBaseSchema.extend({
  facilityId: z.union([z.string(), z.null()]).optional(),
}).superRefine((data, ctx) => {
  // If public is true, facilityId should be null or undefined
  if (
    data.public === true &&
    data.facilityId !== null &&
    data.facilityId !== undefined
  ) {
    ctx.addIssue({
      code: "custom",
      message: "facilityId must be null when public is true",
      path: ["facilityId"],
      input: data,
    });
  }

  // If public is false, facilityId must be a non-empty string
  if (data.public === false) {
    if (
      data.facilityId === null ||
      data.facilityId === undefined ||
      data.facilityId === ""
    ) {
      ctx.addIssue({
        code: "custom",
        message: "facilityId is required when public is false",
        path: ["facilityId"],
        input: data,
      });
    }
  }
});

// Main Event Schema
const EventDtoRawSchema = z
  .object({
    ...sluggedIdSchema,
    tourId: sluggedIdValidator,
    tourName: z.string(),
    tourIsPublic: z.boolean().optional(),
    ...nameSchema,
    ...descriptionSchema,
    imageUrl: z.string(),
    startTime: isoDateWithOffsetValidator,
    endTime: isoDateWithOffsetValidator,
    // Spelled with a lowercase 'z' on the wire; any other casing is
    // stripped by the parse and reads back as undefined.
    eventTimezone: z.string().nullish(),
    ...createdSchema,
    // Not z.enum(Rounds): legacy facility data holds counts outside {1,2,3},
    // and rejecting the event hides it from the operator entirely.
    numberOfRounds: z.number().int().nonnegative(),
    replays: z.number().int().nonnegative(),
    maxUserCount: z.number().int().nonnegative(),
    eventType: z.enum(EventTypeEnum),
    scoringType: z.enum(EventScoringType),
    finalized: z.boolean(),
    participantCount: z.number().int().nonnegative(),
    finishedCount: z.number().int().nonnegative(),
    controls: EventControlsRawSchema,
    rounds: z
      .array(EventRoundRawSchema)
      .nullish()
      .transform((v) => v ?? []),
    availableToSimulators: z.array(z.string()).nullish(),
  })
  .superRefine((data, ctx) => {
    // These cross-field checks only warn. They describe data the API owns and
    // we cannot repair, so treating a mismatch as a parse failure would drop
    // the event from the operator's list rather than show it imperfectly.
    if (data.numberOfRounds < data.rounds.length) {
      console.warn(
        `Warning[${data.id}]: numberOfRounds is ${data.numberOfRounds} but ${data.rounds.length} rounds exist`,
      );
    } else if (
      data.numberOfRounds > data.rounds.length &&
      data.rounds.length === 0
    ) {
      // Warning case: No rounds exist yet, but some are planned
      // This does not trigger a validation error, but you might want to log it
      console.warn(
        `Warning[${data.id}]: numberOfRounds is ${data.numberOfRounds} but no rounds exist yet`,
      );
    } else if (data.numberOfRounds > data.rounds.length) {
      // Warning case: Fewer rounds exist than declared
      // This does not trigger a validation error, but you might want to log it
      console.warn(
        `Warning[${data.id}]: numberOfRounds is ${data.numberOfRounds} but only ${data.rounds.length} rounds exist`,
      );
    }

    // Validate that event type and scoring type are consistent
    // LONG_DRIVE events should use SCORING_LONG_DRIVE
    if (
      data.eventType === EventTypeEnum.LONG_DRIVE &&
      data.scoringType !== EventScoringType.LONG_DRIVE
    ) {
      console.warn(
        `Warning[${data.id}]: long drive event has scoringType ${data.scoringType}`,
      );
    }

    // CLOSEST_TO_PIN events should use SCORING_CLOSEST_TO_PIN
    if (
      data.eventType === EventTypeEnum.CLOSEST_TO_PIN &&
      data.scoringType !== EventScoringType.CLOSEST_TO_PIN
    ) {
      console.warn(
        `Warning[${data.id}]: closest to pin event has scoringType ${data.scoringType}`,
      );
    }

    // Validate that startTime is before endTime
    if (data.startTime > data.endTime) {
      ctx.addIssue({
        code: "custom",
        message: `startTime (${data.startTime.toISO()}) must be before endTime (${data.endTime.toISO()})`,
        path: ["startTime", "endTime"],
        input: data,
      });
    }
  });

// Final schema with transformations
export const TourDtoSchema = TourDtoRawSchema.transform(
  (data): InternalTourDto => ({
    ...data,
    // Transform ISO date string to DateTime object
    created: data.created as DateTime,
  }),
) satisfies z.ZodType<InternalTourDto>;

export type TourDto = z.infer<typeof TourDtoSchema>;

// Array schema for validation of multiple tour objects
export const TourDtoArraySchema = z.array(TourDtoSchema) satisfies z.ZodType<
  TourDto[]
>;

export type EventControlsDto = z.infer<typeof EventControlsRawSchema>;
export type EventRoundDto = z.infer<typeof EventRoundRawSchema>;

// Final schema with transformations
export const EventDtoSchema = EventDtoRawSchema.transform(
  (data): InternalEventDto => ({
    ...data,
    description: data.description || "",
    // Transform dates (already transformed in the raw schema)
    startTime: data.startTime,
    endTime: data.endTime,
    created: data.created as DateTime,
    eventTimezone: data.eventTimezone ?? undefined,
    availableToSimulators: data.availableToSimulators ?? undefined,
    rounds: data.rounds.map((round) => ({
      ...round,
      eventId: data.id,
      courseId: round.courseId ?? undefined,
      swingType: round.swingType ?? undefined,
      holes: round.holes ?? undefined,
    })),
  }),
) satisfies z.ZodType<InternalEventDto>;

export type EventDto = z.infer<typeof EventDtoSchema>;

// Array schema for validation of multiple events
export const EventDtoArraySchema = z.array(EventDtoSchema) satisfies z.ZodType<
  EventDto[]
>;

export const EventLeaderboardResultsSchema =
  EventLeaderboardResultsBaseSchema.transform(
    (data): InternalEventLeaderboardResultsDto => ({
      ...data,
      attemptDateTime: data.attemptDateTime || undefined,
    }),
  ) satisfies z.ZodType<InternalEventLeaderboardResultsDto>;

// SkilledEventLeaderboardResults schema with transformations
export const SkilledEventLeaderboardResultsSchema =
  SkilledEventLeaderboardResultsRawSchema.transform(
    (data): InternalSkilledEventLeaderboardResultsDto => ({
      ...data,
      attemptDateTime: data.attemptDateTime || undefined,
    }),
  ) satisfies z.ZodType<InternalSkilledEventLeaderboardResultsDto>;

// TournamentEventLeaderboardResults schema with transformations
export const TournamentEventLeaderboardResultsSchema =
  TournamentEventLeaderboardResultsRawSchema.transform(
    (data): InternalTournamentEventLeaderboardResultsDto => ({
      ...data,
      attemptDateTime: data.attemptDateTime || undefined,
      initialAttempt: data.initialAttempt || undefined,
    }),
  ) satisfies z.ZodType<InternalTournamentEventLeaderboardResultsDto>;

// IndoorGolfPlayerSchema schema
// export const IndoorGolfPlayerSchema = IndoorGolfPlayerRawSchema.transform(
//   (data): InternalIndoorGolfPlayerDto => ({
//     ...data,
//   })
// ) satisfies z.ZodType<InternalIndoorGolfPlayerDto>;

// Array schemas for lists
export const SkilledEventLeaderboardResultsArraySchema = z.array(
  SkilledEventLeaderboardResultsSchema,
) satisfies z.ZodType<InternalSkilledEventLeaderboardResultsDto[]>;

export const TournamentEventLeaderboardResultsArraySchema = z.array(
  TournamentEventLeaderboardResultsSchema,
) satisfies z.ZodType<InternalTournamentEventLeaderboardResultsDto[]>;

// Export the inferred types
export type EventLeaderboardResultsDto = z.infer<
  typeof EventLeaderboardResultsSchema
>;
export type SkilledEventLeaderboardResultsDto = z.infer<
  typeof SkilledEventLeaderboardResultsSchema
>;
export type TournamentEventLeaderboardResultsDto = z.infer<
  typeof TournamentEventLeaderboardResultsSchema
>;
export type IndoorGolfPlayerDto = z.infer<typeof IndoorGolfPlayerSchema>;

// Usage example:
// const validatedTours = TourDtoArraySchema.parse(yourJsonArray);
